import React from 'react';
import { Box, CircularProgress, Typography, Fade } from '@mui/material';

/**
 * ContentLoader component for displaying loading states during lazy loading
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.loading - Whether content is currently loading
 * @param {React.ReactNode} props.children - Content to display when loaded
 * @param {string} props.message - Optional loading message to display
 * @param {number} props.minHeight - Minimum height of the loader container
 * @param {string} props.size - Size of the loader (small, medium, large)
 */
const ContentLoader = ({ 
  loading = true, 
  children, 
  message = 'Loading content...', 
  minHeight = 200,
  size = 'medium'
}) => {
  const loaderSizes = {
    small: { progress: 24, spacing: 1 },
    medium: { progress: 40, spacing: 2 },
    large: { progress: 60, spacing: 3 }
  };
  
  const { progress, spacing } = loaderSizes[size] || loaderSizes.medium;

  if (loading) {
    return (
      <Fade in={true} style={{ transitionDelay: '300ms' }}>
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: minHeight,
            p: spacing,
            width: '100%'
          }}
          role="alert"
          aria-busy="true"
          aria-label="Loading content"
        >
          <CircularProgress 
            size={progress} 
            thickness={4} 
            color="primary" 
            sx={{ mb: spacing }}
          />
          {message && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              align="center"
            >
              {message}
            </Typography>
          )}
        </Box>
      </Fade>
    );
  }

  return children;
};

export default ContentLoader;
